import { errorFromResponse, notifications } from '~/app/notifications'
import { get } from 'lodash'
import AppButton from '@theme/components/atom/AppButton'
import AppInput from '@theme/components/atom/AppInput'
import AppTitle from '@theme/components/atom/AppTitle'
import BonusClubValuePrinter from '@theme/components/content/BonusClubValuePrinter'
import Breadcrumbs from '@theme/components/molecule/Breadcrumbs'
import CheckoutApplyCredits from '@theme/components/cart/CheckoutApplyCredits'
import CheckoutFreeDelivery from '@theme/components/cart/CheckoutFreeDelivery'
import CheckoutFreeGift from '@theme/components/cart/CheckoutFreeGift'
import CheckoutItemLine from '@theme/components/cart/CheckoutItemLine'
import CheckoutLoginLineHook from '@theme/components/cart/CheckoutLoginLineHook'
import CheckoutNavigationMixin from '~/app/CheckoutNavigationMixin'
import CheckoutStepLoadEventMixin from '~/mixins/CheckoutStepLoadEventMixin'
import CheckoutSteps from '@theme/components/cart/CheckoutSteps'
import Divider from '@theme/components/molecule/Divider'
import EmptyCart from '@theme/components/cart/EmptyCart'
import Gift from '@theme/components/cart/Gift'
import IconLink from '@theme/components/molecule/IconLink'
import LayoutBase from '@theme/components/shop/LayoutBase'
import LayoutCheckout from '@theme/components/cart/LayoutCheckout'
import LoadingMixin from '~/mixins/LoadingMixin'
import Modal from '@theme/components/utils/Modal'
import PageCheckoutItemsBelowItemsHook from '@theme/components/cart/PageCheckoutItemsBelowItemsHook'
import PricesMixin from '~/mixins/PricesMixin'
import ProductsRecommended from '@theme/components/product/ProductsRecommended'
import ProductsVisited from '@theme/components/product/ProductsVisited'
import ProvideValidator from '~/app/ProvideValidator'
import RemoveButton from '@theme/components/atom/RemoveButton'
import SeoMixin from '~/mixins/SeoMixin'
import TopCategories from '@theme/components/category/TopCategories'
import UniqueSellingPoints from '@theme/components/shop/UniqueSellingPoints'
import eventDefinitions from '~/events/eventDefinitions'

export default {
  components: {
    AppButton,
    AppInput,
    AppTitle,
    BonusClubValuePrinter,
    Breadcrumbs,
    CheckoutApplyCredits,
    CheckoutFreeGift,
    CheckoutFreeDelivery,
    CheckoutItemLine,
    CheckoutLoginLineHook,
    CheckoutSteps,
    Gift,
    Divider,
    EmptyCart,
    IconLink,
    LayoutBase,
    LayoutCheckout,
    Modal,
    PageCheckoutItemsBelowItemsHook,
    ProductsRecommended,
    ProductsVisited,
    RemoveButton,
    TopCategories,
    UniqueSellingPoints,
  },
  mixins: [CheckoutNavigationMixin, CheckoutStepLoadEventMixin, LoadingMixin, PricesMixin, ProvideValidator, SeoMixin],
  head() {
    return this.getSeoHead({ yamlKey: 'PageCheckoutItems', noindex: true, follow: true })
  },
  data() {
    return {
      breadcrumbs: [
        {
          title: this.$t('PageCheckoutItems.breadcrumbs.index'),
          path: this.localePath({ name: 'checkout' }),
        },
      ],
      cartName: '',
      giftsVisible: true,
      selectedGiftDetail: null,
      input: {
        promoCode: '',
      },
      promoCodeInputVisible: this.$themeSettings.components.PageCheckoutItems.showPromoCodeInputOnPageLoad,
      promoCodeShowValidIndicator: false,
      saveCartInputVisible: false,
      saveCartValidIndicator: false,
      topCategories: [],
    }
  },
  beforeCreate() {
    this.$store.commit('cart/SET_CHECKOUT_STEP', 0)
  },
  async mounted() {
    await this.$store.dispatch('category/FETCH_TOP_CATEGORIES')
  },
  computed: {
    checkoutItems() {
      return this.$store.state.cart.cartData.items.filter(
        item => !get(item, 'attributes.custom.hideInCheckoutItemsPage', false),
      )
    },
    promoCodeDiscountValue() {
      return this.$currencyFormat(this.$store.state.cart.cartData.prices.couponDiscountWithVat)
    },
    showAvailableGifts() {
      return (
        this.$themeSettings.components.PageCheckoutItems.showAvailableGifts && this.$store.state.globals.orderGiftAt
      )
    },
    showFreeDelivery() {
      return this.$store.state.cart.cartData?.prices?.freeShippingAt
    },
    totalCreditsUsed() {
      return this.$currencyFormat(this.$store.state.cart.cartData.prices.credit)
    },
    showPricesBeforeDiscount() {
      return (
        this.$themeSettings.components.PageCheckoutItems.showPricesBeforeDiscount &&
        this.$store.state.cart.isLoaded &&
        (this.getTotalOriginalPrimaryPrice() || this.getTotalOriginalSecondaryPrice())
      )
    },
    summaryTotalPrimaryPrice() {
      return this.getTotalPriceByType('summaryTotalPrimary')
    },
    summaryTotalSecondaryPrice() {
      return this.getTotalPriceByType('summaryTotalSecondary')
    },
    summaryTotalSecondaryPriceLabel() {
      const { label, withTax } = this.getPriceSettings('summaryTotalSecondary')
      return (
        label && this.$t('PageCheckoutItems.bottomLine.' + (withTax ? 'totalPriceWithTax' : 'totalPriceWithoutTax'))
      )
    },
  },
  methods: {
    onGiftClick(gift) {
      if (window.innerWidth < 992) {
        this.selectedGiftDetail = gift
        this.$refs?.modal?.show()
      }
    },
    getTotalPriceByType(type) {
      const { decimal, show, withTax } = this.getPriceSettings(type)
      const prices = this.$store.state.cart.cartData.prices || {}
      const paymentPrice = get(prices, withTax ? 'paymentPriceWithTax' : 'paymentPriceWithoutTax', 0)
      const shipperPrice = get(prices, withTax ? 'shipperPriceWithTax' : 'shipperPriceWithoutTax', 0)
      const totalPrice = this.getNonformattedPrice({
        prices,
        type,
        ...this.defaultTotalPriceKeys,
      })
      const finalPrice = totalPrice - (paymentPrice || 0) - (shipperPrice || 0)
      return show && this.$currencyFormat(finalPrice, { decimalPlaces: decimal || 0 })
    },
    getTotalOriginalPrimaryPrice() {
      return this.getFormattedPrice({
        prices: this.$store.state.cart.cartData.prices || {},
        type: 'totalPrimary',
        withKey: 'totalOriginalWithTax',
        withoutKey: 'totalOriginalWithoutTax',
      })
    },
    getTotalOriginalSecondaryPrice() {
      return this.getFormattedPrice({
        prices: this.$store.state.cart.cartData.prices || {},
        type: 'totalSecondary',
        withKey: 'totalOriginalWithTax',
        withoutKey: 'totalOriginalWithoutTax',
      })
    },
    toggleGifts() {
      this.giftsVisible = !this.giftsVisible
    },
    next() {
      this.nextStep()
    },
    togglePromoCodeInput() {
      this.promoCodeInputVisible = !this.promoCodeInputVisible
    },
    toggleSaveCartInput() {
      this.saveCartInputVisible = !this.saveCartInputVisible
    },
    async submitPromoCode() {
      if (this.input.promoCode !== '') {
        this.startLoading()
        const response = await this.$store.dispatch('cart/SET_PROMO_CODE', this.input.promoCode)
        if (response && response.status) {
          if (response.status < 500) {
            errorFromResponse(response)
          } else {
            // TODO: Use translatable defaultError() instead of translation here!
            notifications.danger(this.$t('globals.notification.general500.title'))
          }
        } else {
          notifications.success(this.$t('PageCheckoutItems.notifications.promoCode.success'))
          this.promoCodeShowValidIndicator = true
          this.$eventBus.$emit(eventDefinitions.CHECKOUT.STEP_OPTION, {
            number: this.$store.state.cart.checkout.currentStep,
            option: 'promocode_applied',
          })
        }
        this.stopLoading()
      }
    },
    async removePromoCode() {
      this.$refs.removePromoCode.startLoading()
      await this.$store.dispatch('cart/REMOVE_PROMO_CODE')
      this.$eventBus.$emit(eventDefinitions.CHECKOUT.STEP_OPTION, {
        number: this.$store.state.cart.checkout.currentStep,
        option: 'promocode_removed',
      })
      this.stopLoading()
    },
    async submitSaveCart() {
      if (this.cartName) {
        let cartProducts = this.$store.state.cart.cartData.items.map(i => ({ id: i.shoppable.id, amount: i.amount }))
        const response = await this.$store.dispatch('cart/SAVE_CART', {
          name: this.cartName,
          products: cartProducts,
        })
        if (response && response.status) {
          notifications.danger(this.$t('PageCheckoutItems.notifications.saveCart.error'))
          return
        }
        notifications.success(this.$t('PageCheckoutItems.notifications.saveCart.success'))
        this.saveCartValidIndicator = true
      }
    },
    async removeCredits() {
      this.$refs.removeCredits.startLoading()
      this.$store.dispatch('cart/SET_CREDITS', 0)
    },
  },
}
